import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { googleMapsVerifier } from '@/services/google-maps/google-maps-verifier';

interface GooglePlacesSearchProps {
    onAddressSelect: (address: string, city: string, postalCode: string, country: string, houseNumber: string) => void;
    placeholder: string;
    autocompleteText: string;
    value?: string;
    onChange?: (value: string) => void;
    className?: string;
    onGoogleMapsLoadSuccess?: () => void;
    onGoogleMapsLoadFailure?: () => void;
    isLoaded: boolean;
    loadError?: Error;
}

export const GooglePlacesSearchModern: React.FC<GooglePlacesSearchProps> = ({
    onAddressSelect,
    placeholder,
    autocompleteText,
    value,
    onChange,
    className,
    onGoogleMapsLoadSuccess,
    onGoogleMapsLoadFailure,
    isLoaded,
    loadError
}) => {
    const { i18n } = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);
    const [authError, setAuthError] = useState(false);
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const sessionToken = useRef<any>(null);

    // Google appelle cette fonction globale si la clé est invalide / non autorisée
    useEffect(() => {
        const originalAuthFailure = (window as any).gm_authFailure;
        (window as any).gm_authFailure = () => {
            console.error("❌ Google Maps auth failure: invalid API key or restrictions");
            setAuthError(true);
            if (onGoogleMapsLoadFailure) {
                onGoogleMapsLoadFailure();
            }
            if (originalAuthFailure) originalAuthFailure();
        };

        return () => {
            (window as any).gm_authFailure = originalAuthFailure;
        };
    }, [onGoogleMapsLoadFailure]);

    // Vérification centralisée de la clé API
    useEffect(() => {
        const verifyApiKey = async () => {
            const isValid = await googleMapsVerifier.isApiKeyValid();
            if (!isValid) {
                setAuthError(true);
                if (onGoogleMapsLoadFailure) onGoogleMapsLoadFailure();
            }
        };

        verifyApiKey();
    }, [onGoogleMapsLoadFailure]);

    // Timeout de sécurité : si Google Maps n'est pas chargé après 3 secondes, on passe en manuel
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (!isLoaded && !authError) {
                console.warn("⚠️ Timeout Google Maps : bascule vers le mode manuel");
                if (onGoogleMapsLoadFailure) onGoogleMapsLoadFailure();
            }
        }, 3000);

        return () => clearTimeout(timeoutId);
    }, [isLoaded, authError, onGoogleMapsLoadFailure]);

    // Gestion des états de chargement
    useEffect(() => {
        if (loadError) {
            console.error("❌ Erreur de chargement Google Maps :", loadError.message);
            if (onGoogleMapsLoadFailure) onGoogleMapsLoadFailure();
        } else if (authError) {
            console.error("❌ Erreur d'authentification Google Maps");
            if (onGoogleMapsLoadFailure) onGoogleMapsLoadFailure();
        } else if (isLoaded) {
            console.log('✅ Google Maps chargé avec succès (nouvelle API)');
            if (onGoogleMapsLoadSuccess) onGoogleMapsLoadSuccess();
        }
    }, [isLoaded, loadError, authError, onGoogleMapsLoadSuccess, onGoogleMapsLoadFailure]);

    const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        if (onChange) onChange(inputValue);

        if (!inputValue) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        if (isLoaded && window.google?.maps) {
            try {
                // @ts-ignore - Nouvelle API Places
                const { AutocompleteSuggestion, AutocompleteSessionToken } = await google.maps.importLibrary("places");

                if (!sessionToken.current) {
                    sessionToken.current = new AutocompleteSessionToken();
                }

                const request = {
                    input: inputValue,
                    sessionToken: sessionToken.current,
                    includedRegionCodes: ['fr', 'be', 'ch', 'lu', 'es', 'de', 'it', 'nl', 'pt', 'gb'],
                    includedPrimaryTypes: ['street_address', 'route'],
                } as any;

                const { suggestions: fetchedSuggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
                const filtered = (fetchedSuggestions || []).filter((s: any) => {
                    const types = s?.placePrediction?.types || [];
                    return types.includes('street_address') || types.includes('route');
                });
                setSuggestions(filtered);
                setShowSuggestions(true);
            } catch (error) {
                console.error('Google Maps Autocomplete error:', error);
                setSuggestions([]);
                setShowSuggestions(false);
            }
        }
    };

    const handleSelectPrediction = async (placeId: string, description: string) => {
        if (onChange) onChange(description);
        setShowSuggestions(false);

        try {
            const place = new google.maps.places.Place({
                id: placeId,
                requestedLanguage: i18n.language || 'fr',
            });

            const result = await place.fetchFields({
                fields: ['addressComponents', 'formattedAddress', 'location'],
            });

            const placeData = result.place;

            if (placeData && placeData.addressComponents) {
                let streetNumber = '';
                let route = '';
                let city = '';
                let sublocality = '';
                let adminArea2 = '';
                let postalCode = '';
                let country = '';

                for (const component of placeData.addressComponents) {
                    const types = component.types;
                    const value = component.longText || '';

                    if (types.includes('street_number')) streetNumber = value;
                    else if (types.includes('route')) route = value;
                    else if (types.includes('locality')) city = value;
                    else if (types.includes('sublocality') || types.includes('sublocality_level_1')) sublocality = sublocality || value;
                    else if (types.includes('administrative_area_level_2')) adminArea2 = value;
                    else if (types.includes('postal_code')) postalCode = value;
                    else if (types.includes('country')) country = value;
                }

                // Fallback pour adresses italiennes et autres où locality peut être vide
                if (!city && (sublocality || adminArea2)) {
                    city = sublocality || adminArea2;
                }

                if (!route && !city) {
                    console.warn('⚠️ Sélection ignorée: adresse incomplète');
                    if (onChange) onChange('');
                    return;
                }

                const fullAddress = route
                    ? [streetNumber, route].filter(Boolean).join(' ')
                    : placeData.formattedAddress || '';

                onAddressSelect(fullAddress.trim(), city, postalCode, country, streetNumber);

                if (placeData.formattedAddress && onChange) {
                    onChange(placeData.formattedAddress);
                }

                // Refresh session token
                // @ts-ignore
                const { AutocompleteSessionToken } = await google.maps.importLibrary("places");
                sessionToken.current = new AutocompleteSessionToken();
            }
        } catch (error) {
            console.error('Google Maps Place details error:', error);
        }
    };

    if (loadError || authError) return null;

    if (!isLoaded) {
        return (
            <div className="w-full px-3 py-2 border border-border-medium rounded-xl bg-background-secondary text-foreground-secondary animate-pulse text-sm">
                Chargement...
            </div>
        );
    }

    return (
        <div className="relative">
            <input
                ref={inputRef}
                type="text"
                placeholder={placeholder}
                value={value ?? ''}
                onChange={handleInputChange}
                onFocus={() => {
                    if (value && suggestions.length > 0) setShowSuggestions(true);
                }}
                onBlur={() => {
                    setTimeout(() => setShowSuggestions(false), 200);
                }}
                className={className}
                autoComplete="off"
                name="address_autocomplete"
                id="address_autocomplete"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                inputMode="text"
                aria-autocomplete="list"
                data-lpignore="true"
            />

            {showSuggestions && suggestions.length > 0 && (
                <ul
                    className="absolute z-50 w-full border rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto bg-surface border-border"
                >
                    {suggestions.map((suggestion: any) => (
                        <li
                            key={suggestion.placePrediction.placeId}
                            className="px-4 py-2 cursor-pointer text-sm transition-colors hover:bg-background-tertiary text-foreground"
                            onClick={() => handleSelectPrediction(suggestion.placePrediction.placeId, suggestion.placePrediction.text.text)}
                        >
                            {suggestion.placePrediction.text.text}
                        </li>
                    ))}
                </ul>
            )}

            {!value && (
                <p className="text-xs text-foreground-secondary mt-1">{autocompleteText}</p>
            )}
        </div>
    );
};
